"use client";

import React, { useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import LoadingSpinner from "@/components/LoadingSpinner";

// Regular imports - these will be included in the main bundle
import HeavyChart from "@/components/HeavyChart";
import HeavyTable from "@/components/HeavyTable";
import HeavyCalculator from "@/components/HeavyCalculator";

// Dynamic imports - these will be code-split into separate chunks
const DynamicHeavyChart = dynamic(() => import("@/components/HeavyChart"), {
  loading: () => <LoadingSpinner message="Loading Chart Component..." />,
  ssr: false,
});

const DynamicHeavyTable = dynamic(() => import("@/components/HeavyTable"), {
  loading: () => <LoadingSpinner message="Loading Table Component..." />,
  ssr: false,
});

const DynamicHeavyCalculator = dynamic(
  () => import("@/components/HeavyCalculator"),
  {
    loading: () => <LoadingSpinner message="Loading Calculator Component..." />,
    ssr: false,
  }
);

type ComponentType = "chart" | "table" | "calculator";

export default function Home() {
  const [showRegular, setShowRegular] = useState<ComponentType | null>(null);
  const [showDynamic, setShowDynamic] = useState<ComponentType | null>(null);

  const resetComponents = () => {
    setShowRegular(null);
    setShowDynamic(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Image
            className="mx-auto mb-4"
            src="/next.svg"
            alt="Next.js logo"
            width={180}
            height={38}
            priority
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Dynamic Import Bundle Size Demo
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            This demo shows how{" "}
            <code className="bg-gray-200 px-2 py-1 rounded">next/dynamic</code>{" "}
            can help reduce initial bundle size by code-splitting heavy
            components. Open your browser&apos;s DevTools Network tab to see the
            difference!
          </p>
        </div>

        {/* Bundle Size Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-yellow-800 mb-3">
            📊 Bundle Size Impact
          </h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white p-4 rounded">
              <h3 className="font-semibold text-red-600 mb-2">
                ❌ Regular Imports
              </h3>
              <ul className="text-gray-700 space-y-1">
                <li>• All components loaded immediately</li>
                <li>• Chart.js (~200KB) in main bundle</li>
                <li>• Lodash (~70KB) in main bundle</li>
                <li>• date-fns (~20KB) in main bundle</li>
                <li>• Larger initial bundle = slower page load</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded">
              <h3 className="font-semibold text-green-600 mb-2">
                ✅ Dynamic Imports
              </h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Components loaded only when needed</li>
                <li>• Separate chunks for each heavy library</li>
                <li>• Smaller initial bundle</li>
                <li>• Faster initial page load</li>
                <li>• Better Core Web Vitals scores</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          <button
            onClick={resetComponents}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            🔄 Reset All
          </button>
          <Link
            href="/rsc-demo"
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-block"
          >
            🟢 RSC vs Dynamic Demo
          </Link>
          <Link
            href="/analysis"
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-block"
          >
            📊 View Bundle Analysis
          </Link>
        </div>

        {/* Demo Sections */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Regular Imports Section */}
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-red-800 mb-4">
                ❌ Regular Imports (Heavy Bundle)
              </h2>
              <p className="text-red-700 mb-4">
                These components are imported normally and included in the main
                bundle.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => setShowRegular("chart")}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded transition-colors font-medium"
                >
                  📊 Load Chart Component (Chart.js + Lodash + date-fns)
                </button>
                <button
                  onClick={() => setShowRegular("table")}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded transition-colors font-medium"
                >
                  📋 Load Table Component (Lodash + date-fns)
                </button>
                <button
                  onClick={() => setShowRegular("calculator")}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded transition-colors font-medium"
                >
                  🧮 Load Calculator Component (Lodash)
                </button>
              </div>

              <div className="mt-6">
                {showRegular === "chart" && (
                  <HeavyChart title="Regular Import Chart" />
                )}
                {showRegular === "table" && (
                  <HeavyTable title="Regular Import Table" />
                )}
                {showRegular === "calculator" && (
                  <HeavyCalculator title="Regular Import Calculator" />
                )}
              </div>
            </div>
          </div>

          {/* Dynamic Imports Section */}
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-green-800 mb-4">
                ✅ Dynamic Imports (Code-Split)
              </h2>
              <p className="text-green-700 mb-4">
                These components are loaded dynamically and split into separate
                chunks.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => setShowDynamic("chart")}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded transition-colors font-medium"
                >
                  📊 Load Chart Component (Dynamically)
                </button>
                <button
                  onClick={() => setShowDynamic("table")}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded transition-colors font-medium"
                >
                  📋 Load Table Component (Dynamically)
                </button>
                <button
                  onClick={() => setShowDynamic("calculator")}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded transition-colors font-medium"
                >
                  🧮 Load Calculator Component (Dynamically)
                </button>
              </div>

              <div className="mt-6">
                {showDynamic === "chart" && (
                  <DynamicHeavyChart title="Dynamic Import Chart" />
                )}
                {showDynamic === "table" && (
                  <DynamicHeavyTable title="Dynamic Import Table" />
                )}
                {showDynamic === "calculator" && (
                  <DynamicHeavyCalculator title="Dynamic Import Calculator" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-blue-800 mb-3">
            🔍 How to See the Difference
          </h3>
          <ol className="list-decimal list-inside text-blue-700 space-y-2">
            <li>Open your browser&apos;s Developer Tools (F12)</li>
            <li>Go to the Network tab and clear it</li>
            <li>Reload this page and observe the initial bundle size</li>
            <li>
              Click on the &quot;Regular Import&quot; buttons and see no new
              network requests
            </li>
            <li>
              Click on the &quot;Dynamic Import&quot; buttons and see new chunks
              being loaded
            </li>
            <li>Compare the timing and file sizes in the Network tab</li>
          </ol>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-gray-500 border-t border-gray-200 pt-8">
          <p>
            Learn more about{" "}
            <a
              href="https://nextjs.org/docs/advanced-features/dynamic-import"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Next.js Dynamic Imports
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
